import React, { useEffect } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useData } from "../../context/DataContext";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import ImageUpload from "../ImageUpload";
import { SipCalendar, SipDropdown, SipInput, SipTextarea } from "../form/SipControls";
function AddPerBangunan({
  openModal,
  isModalOpen,
  isEditing,
  closeModal,
  form,
  setForm,
  postBangunan,
}) {
  const { petugas, getPetugas, gambar } = useData();
  useEffect(() => {
    getPetugas();
  }, []);

  const resolveFotoUrl = (file) =>
    resolveMediaUrl(`${gambar}bangunan/`, typeof file === "string" ? file : null) || file;
  // Framer Motion variants untuk animasi modal
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: -50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.34, 1.56, 0.64, 1], // Custom spring easing
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: -50,
      transition: {
        duration: 0.3,
        ease: "easeIn",
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { delay: 0.2, duration: 0.3 },
    },
  };

  const closeButtonVariants = {
    hidden: { opacity: 0, rotate: -90 },
    visible: {
      opacity: 1,
      rotate: 0,
      transition: { delay: 0.2, duration: 0.3 },
    },
    hover: {
      rotate: 90,
      transition: { duration: 0.2 },
    },
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    postBangunan();
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (file, fieldName) => {
    setForm((prev) => ({ ...prev, [fieldName]: file }));
  };

  const handleRemoveFile = (fieldName) => {
    setForm((prev) => ({ ...prev, [fieldName]: null }));
  };

  return (
    <>
      <button
        onClick={() => openModal()}
        className="flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-200"
      >
        <FaPlus />
        <span className="font-medium">Tambah Perawatan</span>
      </button>

      {/* ==================== ADD/EDIT MODAL ==================== */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeModal}
            style={{ backdropFilter: "blur(4px)" }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <motion.h3
                    className="text-xl font-semibold text-gray-900"
                    variants={headerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {isEditing
                      ? "Edit Perawatan Bangunan"
                      : "Tambah Perawatan Bangunan"}
                  </motion.h3>
                  <motion.button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
                    variants={closeButtonVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <FaTimes />
                  </motion.button>
                </div>
              </div>

              {/* Form Content */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bagian */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bagian Bangunan *
                    </label>
                    <SipInput
                      name="bagian"
                      value={form.bagian}
                      onChange={handleInputChange}
                      required
                      placeholder="Contoh: Ruang Kelas 1A"
                    />
                  </div>

                  {/* Jenis Kerusakan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Jenis Kerusakan *
                    </label>
                    <SipDropdown
                      name="jenis_kerusakan"
                      value={form.jenis_kerusakan}
                      onChange={handleInputChange}
                      placeholder="Pilih Jenis Kerusakan"
                      filter={false}
                      options={["Atap", "Dinding", "Lantai", "Pintu", "Jendela", "Plafon", "Listrik", "Catu Daya", "Plumbing","Saluran Air"].map((item) => ({
                        label: item,
                        value: item,
                      }))}
                    />
                  </div>

                  {/* Tindakan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tindakan *
                    </label>
                    <SipInput
                      name="tindakan"
                      value={form.tindakan}
                      onChange={handleInputChange}
                      required
                      placeholder="Contoh: Perbaikan genteng bocor"
                    />
                  </div>

                  {/* Petugas */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Petugas/Teknisi <span className="text-red-500">*</span>
                    </label>
                    <SipDropdown
                      name="petugas"
                      value={form.petugas}
                      onChange={handleInputChange}
                      placeholder="Pilih petugas"
                      options={(petugas || []).map((item) => ({
                        label: `${item.nama} - ${item.jabatan}`,
                        value: item.nama,
                      }))}
                    />

                    
                  </div>
                  {/* Tanggal Masuk */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tanggal Masuk *
                    </label>
                    <SipCalendar name="tgl_masuk" value={form.tgl_masuk} onChange={handleInputChange} />
                  </div>

                  {/* Tanggal Selesai */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tanggal Selesai
                    </label>
                    <SipCalendar name="tgl_selesai" value={form.tgl_selesai} onChange={handleInputChange} />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <SipDropdown
                      name="status"
                      value={form.status}
                      onChange={handleInputChange}
                      placeholder="Pilih Status"
                      filter={false}
                      options={[
                        { label: "Pending", value: "pending" },
                        { label: "Proses", value: "proses" },
                        { label: "Selesai", value: "selesai" },
                      ]}
                    />
                  </div>
                </div>

                {/* Keterangan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keterangan
                  </label>
                  <SipTextarea
                    name="ket"
                    value={form.ket}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Keterangan tambahan..."
                  />
                </div>

                {/* Upload Foto */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ImageUpload
                    label="Foto Sebelum"
                    name="foto_sebelum"
                    value={resolveFotoUrl(form.foto_sebelum)}
                    onChange={handleImageChange}
                    onClear={handleRemoveFile}
                    previewSize="sm"
                  />
                  <ImageUpload
                    label="Foto Sesudah"
                    name="foto_sesudah"
                    value={resolveFotoUrl(form.foto_sesudah)}
                    onChange={handleImageChange}
                    onClear={handleRemoveFile}
                    previewSize="sm"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                  <motion.button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300"
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Batal
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:shadow-lg transition-all duration-200"
                    whileHover={{ y: -1, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isEditing ? "Update" : "Simpan"}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default AddPerBangunan;
